import { Info } from 'lucide-react';
import { forwardRef, useContext, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { DialogCloseContext } from './dialog-context';
import { cn } from './utils';

export type ButtonVariant =
  'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'excel';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  closeDialog?: boolean;
  disabledReason?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-brand-600 text-white shadow-sm shadow-brand-700/20 hover:bg-brand-700 focus-visible:ring-brand-500',
  secondary:
    'border-transparent bg-ink-900 text-white shadow-sm shadow-ink-950/15 hover:bg-ink-800 focus-visible:ring-ink-800',
  outline:
    'border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-400',
  ghost:
    'border-transparent bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-slate-400',
  danger:
    'border-transparent bg-rose-600 text-white shadow-sm shadow-rose-950/15 hover:bg-rose-700 focus-visible:ring-rose-500',
  success:
    'border-transparent bg-mint-600 text-white shadow-sm shadow-mint-600/20 hover:bg-mint-500 focus-visible:ring-mint-500',
  excel:
    'border-transparent bg-[#217346] text-white shadow-[0_8px_16px_rgb(33_115_70/0.18)] hover:bg-[#1b633c] focus-visible:ring-[#217346]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'min-h-8 gap-1.5 rounded-lg px-3 py-1.5 text-xs',
  md: 'min-h-10 gap-2 rounded-xl px-4 py-2 text-sm',
  lg: 'min-h-11 gap-2 rounded-xl px-5 py-2.5 text-sm',
  icon: 'size-10 shrink-0 justify-center rounded-xl p-0',
};

function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'size-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent',
        className,
      )}
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    className,
    closeDialog = false,
    disabled,
    disabledReason,
    fullWidth = false,
    leftIcon,
    loading = false,
    onClick,
    rightIcon,
    size = 'md',
    type = 'button',
    variant = 'primary',
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const dialogCloseContext = useContext(DialogCloseContext);
  const disabledReasonId = useId();
  const describedBy =
    disabledReason && isDisabled
      ? [props['aria-describedby'], disabledReasonId].filter(Boolean).join(' ')
      : props['aria-describedby'];
  const button = (
    <button
      {...props}
      ref={ref}
      aria-busy={loading || undefined}
      aria-describedby={describedBy}
      className={cn(
        'inline-flex select-none items-center justify-center border font-semibold leading-none transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={isDisabled}
      type={type}
      onClick={closeDialog ? (dialogCloseContext?.requestClose ?? onClick) : onClick}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );

  if (!disabledReason || !isDisabled) return button;

  return (
    <div
      aria-describedby={disabledReasonId}
      aria-label="Acción bloqueada"
      aria-disabled="true"
      className={cn(
        'group relative',
        fullWidth ? 'flex w-full' : 'inline-flex',
        'rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      )}
      role="group"
      tabIndex={0}
    >
      {button}
      <div
        className="pointer-events-none absolute right-0 bottom-[calc(100%+0.75rem)] z-50 w-80 origin-bottom-right translate-y-1 rounded-2xl bg-slate-950 p-4 text-left text-white opacity-0 shadow-xl shadow-slate-950/20 transition duration-150 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
        id={disabledReasonId}
        role="tooltip"
      >
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-400/20 text-brand-200">
            <Info aria-hidden="true" size={13} strokeWidth={2.5} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold tracking-wide text-brand-100">
              Para habilitar esta acción
            </p>
            <div className="mt-1.5 text-xs leading-5 text-slate-200">{disabledReason}</div>
          </div>
        </div>
        <span
          aria-hidden="true"
          className="absolute right-5 -bottom-1.5 size-3 rotate-45 bg-slate-950"
        />
      </div>
    </div>
  );
});

Button.displayName = 'Button';

export { Spinner };
